import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { HealthIssueDetails } from '@ideals/services/health-checker';
import { ClipboardModule } from '@ideals/ui-components/clipboard';
import { TooltipModule } from '@ideals/ui-components/tooltip';

import { SnackBarModule, SnackBarService } from '../../../snack-bar';
import { TroubleshootingStatus } from '../../services/troubleshooting.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.failed]': 'status() === "failed"',
  },
  imports: [
    ClipboardModule,
    NgTemplateOutlet,
    SnackBarModule,
    TooltipModule,
    TranslateModule,
  ],
  selector: 'ideals-help-troubleshooting',
  standalone: true,
  styleUrl: './help-troubleshooting.component.scss',
  templateUrl: './help-troubleshooting.component.html',
})
export class HelpTroubleshootingComponent {
  readonly issues = input.required<HealthIssueDetails[]>();
  readonly status = input.required<TroubleshootingStatus>();

  readonly downloadReport = output();
  readonly runDiagnostic = output();

  readonly #snackBarService = inject(SnackBarService);
  readonly #translateService = inject(TranslateService);

  protected readonly issuesText = computed(() => this.#stringifyIssues());

  protected onCopyToClipboard(): void {
    const message = this.#translateService.instant('common.TOASTER.copied_to_clipboard') as string;

    this.#snackBarService.openSuccess({ announcementMessage: message });
  }

  #stringifyIssues(): string {
    return this.issues().map((issue) => {
      const details = issue.details
        ?.replace(/\s+/g, '')
        ?.replace(/;/g, '\n') ?? '';

      return [
        this.#translateService.instant(issue.title),
        this.#translateService.instant(issue.description),
        details,
        issue.cta ? this.#translateService.instant(issue.cta) : null,
      ]
        .filter((v) => v)
        .join('\n');
    }).join('\n\n');
  }
}
